import React from "react";
import PropTypes from "prop-types";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { getCoreStructure } from "../../../helpers/tplan/tplan";
import {
    formatLesionVolume,
    formatMarksScore,
    formatProstateCoverage,
    formatVolume,
} from "../helpers";
import {
    EncapsulationConfidenceScoreDefinition,
    FLAIneligibleWarning,
    MAX_PROSTATE_COVERAGE,
    STEP4_METRIC_LABEL,
} from "../constants";
import SpinLoader from "../../__shared__/SpinLoader";
import warningIconSrc from "../../../assets/WarningTriangle.svg";
import QuestionIcon from "../../../ui/icons/QuestionIcon";

function CancerLesionStatistics(props) {
    function getProstateCoverageWarning() {
        const coverage =
            props.currentTreatmentPlan.SavedData
                ?.UserSelectedLesionCoverageOfProstate;
        return coverage > MAX_PROSTATE_COVERAGE;
    }

    function renderSpinLoader() {
        return (
            <SpinLoader
                width="20px"
                height="20px"
                loaderWidth="3px"
                animationDuration="3s"
            />
        );
    }

    const renderFaqTooltip = (props) => (
        // Override default Bootstrap Tooltip styles with custom class defined in index.css
        // Applying inline styles does not work
        <Tooltip {...props} className="r-step4-tooltip-width">
            {EncapsulationConfidenceScoreDefinition}
        </Tooltip>
    );

    const renderWarningTooltip = (props) => (
        <Tooltip {...props}>{FLAIneligibleWarning}</Tooltip>
    );

    const prostateVolume = getCoreStructure(
        props.currentTreatmentPlan.TreatmentPlan
    ).VolumeCC;

    return (
        <div className="-mt-1 grid h-[24%] grid-cols-[1fr_auto_auto] gap-y-1.5 pr-7 pl-10">
            {/* Encapsulation Confidence Score */}
            <div id="encapsulation-confidence-score-label">
                {STEP4_METRIC_LABEL.ENCAPSULATION}
            </div>
            <div className="flex justify-between gap-2 pr-4">
                <span id="encapsulation-confidence-score-value">
                    {props.updatingMargin
                        ? renderSpinLoader()
                        : formatMarksScore(props.userSelectedMarksScore)}
                </span>
                <span>%</span>
            </div>
            <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={renderFaqTooltip}
            >
                <div className="cursor-pointer">
                    <QuestionIcon />
                </div>
            </OverlayTrigger>

            {/* Prostate Volume */}
            <div id="prostate-volume-label">{STEP4_METRIC_LABEL.PROSTATE}</div>
            <div className="flex justify-between gap-2 pr-4">
                <span id="prostate-volume-value">
                    {props.updatingMargin
                        ? renderSpinLoader()
                        : formatVolume(prostateVolume)}
                </span>
                <span>cc</span>
            </div>
            <div></div>

            {/* Lesion Volume */}
            <div id="lesion-volume-label">{STEP4_METRIC_LABEL.LESION}</div>
            <div className="flex justify-between gap-2 pr-4">
                <span id="lesion-volume-value">
                    {props.updatingMargin
                        ? renderSpinLoader()
                        : formatLesionVolume(props.userSelectedLesionVolume)}
                </span>
                <span>cc</span>
            </div>
            <div></div>

            {/* High Risk Volume */}
            {/* We cannot use the term "Tumor Volume" yet */}
            <div id="high-risk-volume-label">
                {STEP4_METRIC_LABEL.TUMOR_VOL}
            </div>
            <div className="flex justify-between gap-2 pr-4">
                {props.updatingMargin ? (
                    renderSpinLoader()
                ) : props.tumorVolume != null ? (
                    <>
                        <span id="high-risk-volume-value">
                            {formatVolume(props.tumorVolume)}
                        </span>
                        <span id="high-risk-volume-unit">cc</span>
                    </>
                ) : (
                    <span
                        id="high-risk-volume-value-placeholder"
                        className="mx-auto"
                    >
                        -
                    </span>
                )}
            </div>
            <div id="high-risk-volume-empty-cell" />

            {/* Prostate Covered by Lesion */}
            <div id="prostate-coverage-label">
                {STEP4_METRIC_LABEL.PROSTATE_COVERED}
            </div>
            <div className="flex justify-between gap-2 pr-4">
                <span id="prostate-coverage-value">
                    {props.updatingMargin
                        ? renderSpinLoader()
                        : formatProstateCoverage(
                              props.userSelectedProstateCoverage
                          )}
                </span>
                <span>%</span>
            </div>
            <div>
                {getProstateCoverageWarning() && (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderWarningTooltip()}
                    >
                        <img
                            id="prostate-coverage-warning"
                            className="w-6"
                            src={warningIconSrc}
                            alt="Warning triangle"
                        />
                    </OverlayTrigger>
                )}
            </div>
        </div>
    );
}

CancerLesionStatistics.propTypes = {
    currentTreatmentPlan: PropTypes.object,
    updatingMargin: PropTypes.bool,
    userSelectedLesionVolume: PropTypes.number,
    userSelectedMarksScore: PropTypes.number,
    userSelectedProstateCoverage: PropTypes.number,
    tumorVolume: PropTypes.number,
};

export default CancerLesionStatistics;
